import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronDown, Menu, Rocket, Search, UserPlus } from "lucide-react";
import { cn } from "@/cn";
import { MobileSheet } from "@/components/mobile-sheet";
import {
  SETTINGS_GET_STARTED,
  filterSettingsSections,
  groupContainsPath,
  isSettingsActivePath,
  isSettingsNavGroup,
  loadExpandedSettingsGroups,
  persistExpandedSettingsGroups,
  type SettingsNavGroup,
  type SettingsNavLink,
} from "@/lib/settings-nav";

function SettingsLink({
  item,
  pathname,
  dense,
}: {
  item: SettingsNavLink;
  pathname: string;
  dense?: boolean;
}) {
  const active = isSettingsActivePath(item.href, pathname);
  return (
    <Link
      to={item.href}
      className={cn(
        "block truncate rounded-md transition-colors",
        dense ? "px-3 py-1.5 text-[13px]" : "px-3 py-2 text-sm",
        active
          ? "bg-brand-soft font-medium text-brand"
          : "text-muted hover:bg-surface-hover hover:text-foreground",
      )}
    >
      {item.label}
    </Link>
  );
}

function SettingsGroup({
  group,
  pathname,
  open,
  onToggle,
}: {
  group: SettingsNavGroup;
  pathname: string;
  open: boolean;
  onToggle: () => void;
}) {
  const hasActive = groupContainsPath(group, pathname);
  return (
    <div className="mb-0.5">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-1 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors",
          hasActive
            ? "text-foreground"
            : "text-muted-foreground hover:bg-surface-hover hover:text-foreground",
        )}
      >
        <span className="flex-1 truncate">{group.label}</span>
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <div className="ml-2 flex flex-col gap-0.5 border-l border-border pl-2">
          {group.items.map((item) => (
            <SettingsLink key={item.href} item={item} pathname={pathname} dense />
          ))}
        </div>
      )}
    </div>
  );
}

export function SettingsLayout() {
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(() => loadExpandedSettingsGroups(pathname));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of loadExpandedSettingsGroups(pathname)) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      if (changed) persistExpandedSettingsGroups(next);
      return changed ? next : prev;
    });
    setMobileNavOpen(false);
  }, [pathname]);

  function toggleGroup(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      persistExpandedSettingsGroups(next);
      return next;
    });
  }

  const sections = filterSettingsSections(query);
  const showGetStarted =
    !query.trim() ||
    SETTINGS_GET_STARTED.label.toLowerCase().includes(query.trim().toLowerCase());

  const nav = (
    <>
      <div className="mb-4 flex items-center gap-2 px-1">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground transition hover:text-brand"
        >
          <ArrowLeft className="size-4" />
          Settings
        </Link>
      </div>

      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search settings"
          className="w-full rounded-md border border-border bg-bg py-2 pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
        />
      </div>

      <nav className="flex flex-1 flex-col gap-4 overflow-y-auto pb-4">
        {showGetStarted && (
          <Link
            to={SETTINGS_GET_STARTED.href}
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
              isSettingsActivePath(SETTINGS_GET_STARTED.href, pathname)
                ? "bg-brand-soft font-medium text-brand"
                : "text-muted hover:bg-surface-hover hover:text-foreground",
            )}
          >
            <Rocket className="size-3.5 shrink-0" />
            {SETTINGS_GET_STARTED.label}
          </Link>
        )}

        {sections.map((section) => (
          <div key={section.id}>
            <p className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {section.label}
            </p>
            <div className="flex flex-col gap-0.5">
              {section.items.map((entry) =>
                isSettingsNavGroup(entry) ? (
                  <SettingsGroup
                    key={entry.id}
                    group={entry}
                    pathname={pathname}
                    open={expanded.has(entry.id) || Boolean(query.trim())}
                    onToggle={() => toggleGroup(entry.id)}
                  />
                ) : (
                  <SettingsLink key={entry.href} item={entry} pathname={pathname} />
                ),
              )}
            </div>
          </div>
        ))}
      </nav>

      <div className="mt-auto border-t border-border pt-3">
        <Link
          to="/settings/users"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-brand px-3 py-2.5 text-sm font-medium text-brand-foreground transition hover:opacity-90"
        >
          <UserPlus className="size-4" />
          Add Teammates
        </Link>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <div className="flex items-center justify-between border-b border-border px-4 py-3 md:hidden">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm text-foreground"
          aria-expanded={mobileNavOpen}
        >
          <Menu className="size-4" />
          Settings menu
        </button>
        <Link to="/" className="text-sm text-brand hover:underline">
          Exit settings
        </Link>
      </div>

      <aside className="hidden w-full shrink-0 flex-col border-r border-border bg-surface/40 p-4 md:sticky md:top-0 md:flex md:h-screen md:w-64 md:overflow-y-auto">
        {nav}
      </aside>

      <MobileSheet
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        title="Settings"
        variant="full"
        reserveBottomNav={false}
      >
        <div className="flex h-full flex-col p-4">{nav}</div>
      </MobileSheet>

      <main className="flex-1 overflow-x-hidden px-6 py-8">
        <div className="mx-auto max-w-4xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
